import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, CalendarHeart } from "lucide-react";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Podcasts", id: "podcasts" },
  { label: "Services", id: "services" },
  { label: "Courses", id: "courses" },
  { label: "Stories", id: "stories" },
];

export const Header = ({ onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (window.__lenis) window.__lenis.scrollTo(`#${id}`, { offset: -80 });
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled
          ? "bg-sand/80 backdrop-blur-xl border-ink/10 shadow-lift"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
        <button
          data-testid="nav-logo"
          onClick={() => go("top")}
          className="flex items-center gap-2.5"
        >
          <img
            src="/assets/logo-circle.png"
            alt="The Birth Life logo"
            className="h-10 w-10 rounded-full border border-ink/10 object-cover"
            data-testid="nav-logo-image"
          />
          <span className="font-display text-2xl font-semibold tracking-tight text-ink">
            The Birth <span className="italic text-terra">Life</span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 md:flex" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="font-outfit text-sm font-medium text-plumsoft transition-colors duration-300 hover:text-terra"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-book-cta"
            onClick={() => onBook()}
            className="group inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-outfit text-sm font-semibold text-sand transition-all duration-300 hover:bg-terra"
          >
            <CalendarHeart className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
            Book Consultation
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-ink"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-ink/10 bg-sand/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="rounded-lg px-3 py-3 text-left font-outfit text-base font-medium text-plumsoft hover:bg-cream"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-book-cta"
                onClick={() => {
                  setOpen(false);
                  onBook();
                }}
                className="mt-2 rounded-full bg-ink px-5 py-3 font-outfit text-sm font-semibold text-sand"
              >
                Book Consultation
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
